import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "galarya_token";

// Gate D §30: higiene de sesión en equipo compartido. Al cerrar sesión o cambiar de usuario se
// borra TODO lo tenant-sensitive que exista de verdad (token + selección de sitio/persona/caso).
const CLAVES_TENANT = [TOKEN_KEY, "galarya_sitio_sel", "galarya_persona_sel", "galarya_caso_sel"];
export function limpiarSesionLocal() {
  CLAVES_TENANT.forEach((k) => localStorage.removeItem(k));
  try {
    sessionStorage.clear();
  } catch (e) {
    /* sessionStorage puede no existir */
  }
}

const api = axios.create({ baseURL: API });

// GUARD DEMO: mientras un escenario demo está activo, NINGUNA escritura real (POST/PUT/PATCH/DELETE)
// puede llegar al backend. Se activa/desactiva SÍNCRONAMENTE desde ScenarioContext. Rechazo visible.
let _demoGuard = false;
const MUTANTES = new Set(["post", "put", "patch", "delete"]);
export function setDemoGuard(on) { _demoGuard = !!on; }
export function isDemoGuardOn() { return _demoGuard; }

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (_demoGuard && MUTANTES.has(String(config.method || "").toLowerCase())) {
    return Promise.reject(new Error("[DEMO] escritura real bloqueada"));
  }
  return config;
});

// Sesión revocada server-side (security_stamp / cuenta o tenant inactivo): no mostrar datos
// del usuario anterior. Se limpia y se manda a login.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    // El 401 de estas rutas es un error de credencial del propio formulario, no una sesión
    // revocada: se maneja inline y NO debe expulsar al usuario. (`/hard-delete` reautentica al
    // supervisor dentro de la request; al alcanzar el umbral el backend sí invalida la sesión y
    // el siguiente request cae en la rama global.)
    const propioFormulario = ["/auth/login", "/auth/cambiar-password", "/hard-delete"]
      .some((p) => url.includes(p));
    if (status === 401 && !propioFormulario) {
      limpiarSesionLocal();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);

// INC298278 prevención (v2.21.4): la cola de alarmas se ordena en el backend ANTES del límite y,
// si hay más casos que el límite, responde la continuación en cabecera. La consola la sigue hasta
// agotar la cola. Cuando NO se puede agotar (tope de páginas, exceso de casos sin severidad
// guardada o una página que falla), el resultado se marca como INCOMPLETO para que la pantalla lo
// diga: una alarma que no aparece en una lista incompleta NO está cerrada, sólo no se alcanzó.
const ALARMAS_MAX_PAGINAS = 8;
const ALARMAS_EN_VUELO = new Map();

export const MOTIVOS_LISTA = {
  tope_paginas: "se alcanzó el tope de páginas del recorrido",
  legacy_excedido: "hay demasiados casos sin severidad guardada",
  pagina_fallida: "falló una página intermedia del recorrido",
};

export function textoListaIncompleta(casos) {
  const motivos = (casos && casos.motivos) || [];
  if (!motivos.length) return "";
  return `Lista incompleta: ${motivos.map((m) => MOTIVOS_LISTA[m] || m).join(" y ")}. ` +
         "Faltan casos por mostrar; los que no aparecen NO están cerrados.";
}

async function recorrerAlarmas(params) {
  const vistos = new Set();
  const casos = [];
  const motivos = [];
  let desde = null;
  let paginas = 0;
  for (let i = 0; i < ALARMAS_MAX_PAGINAS; i += 1) {
    let r;
    try {
      r = await api.get("/alarmas", { params: { ...params, ...(desde ? { desde } : {}) } });
    } catch (e) {
      if (!paginas) throw e;            // ninguna página: es un fallo de lectura, no una lista parcial
      motivos.push("pagina_fallida");
      desde = null;
      break;
    }
    paginas += 1;
    for (const a of Array.isArray(r.data) ? r.data : []) {
      if (a && a.id && !vistos.has(a.id)) { vistos.add(a.id); casos.push(a); }  // unión sin duplicados
    }
    if (r.headers?.["x-galarya-sin-orden-excedido"] === "1" && !motivos.includes("legacy_excedido")) {
      motivos.push("legacy_excedido");
    }
    desde = r.headers?.["x-galarya-siguiente"] || null;
    if (!desde) break;
  }
  if (desde) motivos.push("tope_paginas");  // quedaba continuación al agotar las páginas permitidas
  casos.paginas = paginas;
  casos.motivos = motivos;
  casos.incompleta = motivos.length > 0;
  return casos;
}

export function getAlarmas(params = {}) {
  // Un sondeo por combinación de parámetros y sesión: si llega otro mientras el anterior sigue en
  // vuelo, comparte la MISMA lectura en vez de superponerse.
  const clave = `${(localStorage.getItem(TOKEN_KEY) || "").slice(-16)}|${JSON.stringify(params)}`;
  const enCurso = ALARMAS_EN_VUELO.get(clave);
  if (enCurso) return enCurso;
  const p = recorrerAlarmas(params).finally(() => ALARMAS_EN_VUELO.delete(clave));
  ALARMAS_EN_VUELO.set(clave, p);
  return p;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
